// ============================================================================
// MarkdownText — zero-dep inline markdown renderer for the AI analysis output.
// Handles the subset of markdown the AI prompt actually returns:
//   - **bold**
//   - ### Heading 3 / ## Heading 2
//   - - Bullet lists / * Bullet lists
//   - 1. Numbered lists
//   - Paragraphs with line breaks
//   - Inline code with `backticks`
//
// Replaces the previous `whitespace-pre-wrap` raw text dump that was showing
// `**bold**` / `### Heading` as literal characters and killing the $1B feel.
// ============================================================================
import React from 'react';

// Inline parser — handles **bold**, `code`, emojis pass-through
const renderInline = (text) => {
  if (!text) return null;
  const parts = [];
  let lastIndex = 0;
  let keyCounter = 0;

  // Combined regex: bold OR inline code
  const re = /(\*\*[^*]+?\*\*)|(`[^`]+?`)/g;
  let match;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }
    const token = match[0];
    if (token.startsWith('**')) {
      parts.push(<strong key={`b-${keyCounter++}`} className="text-white font-semibold">{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('`')) {
      parts.push(<code key={`c-${keyCounter++}`} className="px-1.5 py-0.5 rounded bg-white/[0.04] border border-white/10 text-cyan-300 text-[0.9em] font-mono">{token.slice(1, -1)}</code>);
    }
    lastIndex = match.index + token.length;
  }
  if (lastIndex < text.length) parts.push(text.substring(lastIndex));
  return parts;
};

const MarkdownText = ({ text, className = '' }) => {
  if (!text) return null;

  const lines = text.split('\n');
  const blocks = [];
  let listBuffer = [];
  let listType = null; // 'ul' | 'ol'
  let inBlockSep = false;

  const flushList = () => {
    if (listBuffer.length === 0) return;
    const Tag = listType === 'ol' ? 'ol' : 'ul';
    blocks.push(
      <Tag key={`l-${blocks.length}`} className={`pl-5 my-3 space-y-1.5 ${listType === 'ol' ? 'list-decimal' : 'list-disc'} marker:text-cyan-400/50`}>
        {listBuffer.map((item, i) => (
          <li key={i} className="text-gray-300 leading-relaxed pl-1">
            {renderInline(item)}
          </li>
        ))}
      </Tag>
    );
    listBuffer = [];
    listType = null;
  };

  for (const line of lines) {
    const trimmed = line.trim();

    // Block separator like '— EXECUTION_DECISION_BLOCK —' or '---'
    if (/^[—\-_]{3,}\s*$/.test(trimmed) || /^[—\-]\s+[A-Z_]+\s+[—\-]\s*$/.test(trimmed)) {
      flushList();
      blocks.push(
        <div key={`sep-${blocks.length}`} className="my-5 pt-3 border-t border-white/10 text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400">
          {trimmed.replace(/[—\-_]/g, '').trim() || 'execution decision'}
        </div>
      );
      inBlockSep = true;
      continue;
    }

    // Heading 3
    if (trimmed.startsWith('### ')) {
      flushList();
      blocks.push(
        <h3 key={`h3-${blocks.length}`} className="text-sm font-bold text-white mt-5 mb-2 flex items-center gap-2">
          <span className="w-1 h-4 rounded-full bg-gradient-to-b from-cyan-400 to-purple-400" />
          {renderInline(trimmed.slice(4))}
        </h3>
      );
      continue;
    }

    // Heading 2
    if (trimmed.startsWith('## ')) {
      flushList();
      blocks.push(
        <h2 key={`h2-${blocks.length}`} className="text-base font-bold text-white mt-6 mb-3 flex items-center gap-2">
          <span className="w-1.5 h-5 rounded-full bg-gradient-to-b from-cyan-400 to-purple-400" />
          {renderInline(trimmed.slice(3))}
        </h2>
      );
      continue;
    }

    // Heading 1
    if (trimmed.startsWith('# ')) {
      flushList();
      blocks.push(
        <h1 key={`h1-${blocks.length}`} className="text-lg font-black text-white mt-6 mb-3">
          {renderInline(trimmed.slice(2))}
        </h1>
      );
      continue;
    }

    // Numbered list with "N. **Heading**" (treat as h3-like)
    const numHeading = /^(\d+)\.\s+\*\*(.+?)\*\*\s*$/.exec(trimmed);
    if (numHeading) {
      flushList();
      blocks.push(
        <h3 key={`nh-${blocks.length}`} className="text-sm font-bold text-white mt-5 mb-2 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 text-[10px] font-mono">
            {numHeading[1]}
          </span>
          {renderInline(numHeading[2])}
        </h3>
      );
      continue;
    }

    // Bullet list
    if (/^[-*]\s+/.test(trimmed)) {
      if (listType !== 'ul') flushList();
      listType = 'ul';
      listBuffer.push(trimmed.replace(/^[-*]\s+/, ''));
      continue;
    }

    // Numbered list
    if (/^\d+\.\s+/.test(trimmed)) {
      if (listType !== 'ol') flushList();
      listType = 'ol';
      listBuffer.push(trimmed.replace(/^\d+\.\s+/, ''));
      continue;
    }

    // Key: value pattern (e.g. "Decision: WAIT") inside the execution block
    const kv = /^([A-Z][A-Z0-9_]*)\s*:\s*(.+)$/.exec(trimmed);
    if (kv && inBlockSep) {
      flushList();
      blocks.push(
        <div key={`kv-${blocks.length}`} className="flex items-baseline gap-3 my-1 text-sm">
          <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-gray-500 min-w-[140px]">{kv[1].replace(/_/g, ' ')}</span>
          <span className="text-white font-mono font-semibold">{renderInline(kv[2])}</span>
        </div>
      );
      continue;
    }

    // Empty line — flush list, no paragraph
    if (!trimmed) {
      flushList();
      continue;
    }

    // Plain paragraph
    flushList();
    blocks.push(
      <p key={`p-${blocks.length}`} className="text-sm text-gray-300 leading-relaxed my-2">
        {renderInline(trimmed)}
      </p>
    );
  }
  flushList();

  return <div className={`space-y-0 ${className}`}>{blocks}</div>;
};

export default MarkdownText;
